import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useDispatch } from "react-redux";
import { Alert } from "~/common/mui";
import { Trans, useTranslation } from "react-i18next";
import { ProductComparisonDrawer } from "./ProductComparisonDrawer";
import { QuickEntryPopup } from "~/features/quickEntry/v2/QuickEntryPopup";
import { hasProductConfiguredPC } from "~/features/quickEntry/utils/helpers";
import { useNumericScreenSize, SIZE_PHONE } from "~/common/hooks/useNumericScreenSize";
import { useAddProductsToComparisonMutation, useProductComparisonQuery } from "../api/productComparisonQuery";
import { addBaseProductDataAction } from "~/domains/product/redux/basicProductActions";
import { transformPdpProduct } from "~/features/productDetails/transformers/PdpPageProductDataTransformer";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import {
  HOMEPAGE,
  PRODUCT_DETAILS_PAGE,
  PRODUCT_LIST_PAGE,
  SALES_CAMPAIGN_PAGE,
  SEARCH_PAGE,
  WISHLIST_PAGE,
} from "~/utils/url/staticUrls";

export const STORAGE_KEY = "product-comparison-drawer-closed";
let openDrawerRef: (() => void) | null = null;
let closeDrawerRef: (() => void) | null = null;
export const openProductComparisonDrawer = () => {
  openDrawerRef?.();
};
export const closeProductComparisonDrawer = () => {
  closeDrawerRef?.();
};

export const ProductComparisonDrawerContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const comparisonQuery = useProductComparisonQuery();
  const { data: comparisonData } = comparisonQuery;
  const productCodes = comparisonData?.productCodes ?? [];
  const compareListData = useMemo(
    () => comparisonData?.products.map((item) => transformPdpProduct(item, null)) ?? [],
    [comparisonData?.products],
  );
  const isCompareDataLoaded = !userIsLoggedIn() || !comparisonQuery.isPending;
  const addProductsMutation = useAddProductsToComparisonMutation();
  const [isQuickEntryOpen, setQuickEntryOpen] = useState(false);
  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= SIZE_PHONE;

  const currentPath = window.location.pathname;

  const isAllowedPage =
    currentPath === HOMEPAGE ||
    currentPath.startsWith(SEARCH_PAGE) ||
    currentPath.startsWith(WISHLIST_PAGE) ||
    currentPath.startsWith(PRODUCT_LIST_PAGE) ||
    currentPath.startsWith(PRODUCT_DETAILS_PAGE) ||
    currentPath.includes(SALES_CAMPAIGN_PAGE);
  const products = useMemo(
    () =>
      compareListData.map((product) => ({
        code: product.code,
      })),
    [compareListData],
  );

  // Remember closed state in session storage
  const [isDrawerOpen, setDrawerOpen] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return localStorage.getItem(STORAGE_KEY) !== "true";
  });

  const handleClose = () => {
    setDrawerOpen(false);
    localStorage.setItem(STORAGE_KEY, "true");
  };

  const handleOpenQuickEntry = () => {
    setQuickEntryOpen(true);
  };

  useEffect(() => {
    if (compareListData.length > 0) {
      dispatch(addBaseProductDataAction(compareListData));
    }
  }, [compareListData, dispatch]);

  useEffect(() => {
    if (!isCompareDataLoaded) {
      return;
    }
    if (productCodes.length === 0) {
      setDrawerOpen(false);
      return;
    }

    if (isAllowedPage) {
      const wasClosed = localStorage.getItem(STORAGE_KEY) === "true";
      setDrawerOpen(!wasClosed);
    } else {
      setDrawerOpen(false);
    }
  }, [isCompareDataLoaded, productCodes.length, isAllowedPage]);

  useEffect(() => {
    openDrawerRef = () => {
      localStorage.removeItem(STORAGE_KEY);
      setDrawerOpen(true);
    };
    closeDrawerRef = () => {
      localStorage.setItem(STORAGE_KEY, "true");
      setDrawerOpen(false);
    };

    return () => {
      openDrawerRef = null;
      closeDrawerRef = null;
    };
  }, []);

  return (
    <>
      <ProductComparisonDrawer
        open={isAllowedPage && isDrawerOpen && compareListData.length > 0}
        products={products}
        maxSize={comparisonData?.maxSize ?? 0}
        isMobile={isMobile}
        compareLabel={t("product.comparison.compare.text")}
        addProductLabel={t("comparisonPage.addProduct.title")}
        addButtonLabel={t("comparisonPage.modal.add.submitBtn")}
        onClose={handleClose}
        onOpenQuickEntry={handleOpenQuickEntry}
      />

      {isQuickEntryOpen && (
        <QuickEntryPopup
          onClose={() => setQuickEntryOpen(false)}
          maxAmount={(comparisonData?.maxSize ?? 0) - compareListData.length}
          addButtonLabel={t("comparisonPage.modal.add.submitBtn")}
          validateRow={(row, itemsPopup = []) => {
            const errors: ReactNode[] = [];

            const filteredItemsPopup = itemsPopup
              .filter((item) => item.id !== row.id)
              .map((item) => item?.product?.code);

            const comparisonCodes = compareListData.map((p) => p.code);

            const dataPagePlusPopup = [...comparisonCodes, ...filteredItemsPopup];

            if (dataPagePlusPopup.includes(row.product.code)) {
              errors.push(
                <Alert className="product-id" severity="error">
                  <Trans i18nKey="bulkUpload.productComparison.error.repeatedProduct">
                    The product <span>{{ designation: row.product.designation }}</span> is already in comparison list.
                    Choose different product to compare.
                  </Trans>
                </Alert>,
              );
            }

            return errors;
          }}
          onAdd={async (form, setIsSending, onClosePopup) => {
            const items = form.items.filter(hasProductConfiguredPC);
            const arrProd = items.map((item) => item.product.code);

            setIsSending(true);
            try {
              const result = await addProductsMutation.mutateAsync(arrProd);
              if (result.kind === "bulk-added" && result.products.length > 0) {
                dispatch(
                  addBaseProductDataAction(
                    result.products.map((product) => transformProduct(product)),
                    true,
                  ),
                );
              }

              // Re-open drawer when user adds products manually.
              localStorage.removeItem(STORAGE_KEY);
              setDrawerOpen(true);
              onClosePopup();
            } finally {
              setIsSending(false);
            }
          }}
        />
      )}
    </>
  );
};
